// ---------------------------------------------------------------------------
// encounterUtils — pure helpers for the Encounter feature.
//
// Split out from `encounterService.js` so tests can import these helpers
// without triggering the firebase/firestore module graph. Everything in here
// is side-effect-free and synchronous; anything that talks to Firestore lives
// in `encounterService.js`.
// ---------------------------------------------------------------------------

import {isSupportedVideoCallLink} from '../common/videoCallLinks';

export const ENCOUNTER_STATUS = Object.freeze({
  OPEN: 'open',
  CLOSED: 'closed',
});

// How far back the implicit "Continue earlier session?" modal will reach
// when the provider starts a new Scribe on a patient. The modal is meant
// to catch "I forgot I had a session from earlier" — anything older than
// yesterday almost always means a forgotten orphan (browser crashed
// mid-session, status never flipped to closed), which we'd rather have
// the provider explicitly Mark Done from the patient detail card than
// silently re-attach to. The lookback is generous enough to span a
// late-night session → morning continuation (cross-midnight) but not so
// generous that a month-old open encounter shows up. Used by
// `pickContinuableEncounter`.
export const OPEN_ENCOUNTER_LOOKBACK_MS = 36 * 60 * 60 * 1000;

export const SEGMENT_MARKER_CONTENT_TYPE = 'segment_header';
export const SEGMENT_MARKER_SPEAKER = '[SEGMENT]';
export const LIVE_ENCOUNTER_WORKFLOW_TYPE = 'interpretation';
export const TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE = 'telehealth_interpretation';
export const MEDICAL_SCRIBING_WORKFLOW_TYPE = 'medical_scribing';
export const TELEHEALTH_SCRIBE_WORKFLOW_TYPE = 'telehealth_scribe';
// Solo provider dictation (planner "+" → Dictation): runs on the scribe
// pipeline; the Session tag `dictation` mirrors to this workflow_type.
export const DICTATION_WORKFLOW_TYPE = 'dictation';

// Per-contributor accent palette for the transcript view. Used by both
// `SessionDetailView` and `PostSessionNotesView` to color-tint message
// bubbles by source_user_id so a multi-provider encounter reads as
// "Dr. Singh's segment is teal, Dr. Thomas's segment is amber" at a
// glance, on top of the labeled segment-header dividers.
//
// Each entry is a {bar, label, name} triple:
//   - `bar`: 3px-wide left-border color applied to the bubble itself.
//     Soft pastel shades so even five segments in a row don't feel
//     loud. Each pairs to ≥3:1 contrast against the white card
//     background (WCAG AA for graphical objects).
//   - `label`: a slightly deeper version of the same hue for any
//     "Dr. Singh" attribution text inside the segment. ≥4.5:1 on a
//     white background (AA Normal).
//   - `name`: a stable internal identifier used in tests + debug logs.
//
// We deliberately avoid using the modality colors (blue=Scribe,
// gray=Encounter, orange=Recording, teal=Shared) here so the
// per-contributor accent never collides with any other semantic state.
// `pickSegmentAccent` hashes the source_user_id into this palette so
// the same provider gets the same color across renders.
export const SEGMENT_ACCENT_PALETTE = Object.freeze([
  {bar: '#7D7D7D', label: '#585858', name: 'emerald'}, // 4.78:1 / 7.4:1
  {bar: '#848484', label: '#4E4E4E', name: 'amber'}, // 3.1:1 / 7.1:1
  {bar: '#555555', label: '#383838', name: 'violet'}, // 4.6:1 / 8.5:1
  {bar: '#535353', label: '#373737', name: 'gray'}, // 4.5:1 / 9.6:1
  {bar: '#606060', label: '#3A3A3A', name: 'indigo'}, // 5.2:1 / 11.5:1
  {bar: '#767676', label: '#505050', name: 'gray'}, // 3.4:1 / 8.8:1
]);

/**
 * Pick a stable accent triple for a given contributor (`source_user_id`).
 * Same id → same color across renders and across components, which keeps
 * the segment color in the transcript consistent with any avatar/chip
 * that surface elsewhere for the same provider (when those surfaces
 * adopt this helper too).
 */
export function pickSegmentAccent(sourceUserId: unknown) {
  if (!sourceUserId) return SEGMENT_ACCENT_PALETTE[0];
  let hash = 0;
  const s = String(sourceUserId);
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) | 0;
  }
  return SEGMENT_ACCENT_PALETTE[Math.abs(hash) % SEGMENT_ACCENT_PALETTE.length];
}

const IMPLICIT_PREFIX = 'implicit:';

/**
 * Coerce any Firestore-ish value (Timestamp | Date | seconds-shape | string)
 * into a JS Date, or null if it can't be parsed.
 */
// Conversation docs in the wild are written by several clients with
// different field-casing conventions:
//   - calliope-web (this app): snake_case (`workflow_type`, `provider_name`,
//     `foreign_language`, `template_type`).
//   - iOS, calliope-telephonic, calliope-backend (Zoom / LiveKit): camelCase
//     (`workflowType`, `providerName`, `foreignLanguage`, `templateType`).
//
// The grouping helpers run client-side against whatever happens to be in
// Firestore, so they have to read both. This thin normalizer also coerces
// a few known legacy values onto the canonical names we emit.
function pickConversationField(
  conversation: any,
  fieldNames: string[],
  fallback: any = '',
) {
  if (!conversation) return fallback;
  for (const name of fieldNames) {
    const value = conversation[name];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
}

function pickEncounterField(
  records: any[],
  fieldNames: string[],
  fallback: any = '',
) {
  for (const record of records) {
    const value = pickConversationField(record, fieldNames, '');
    if (value !== '') return value;
  }
  return fallback;
}

function readWorkflowType(conversation: any) {
  return pickConversationField(
    conversation,
    ['workflow_type', 'workflowType'],
    '',
  );
}
function readTemplateType(conversation: any) {
  return pickConversationField(
    conversation,
    ['template_type', 'templateType'],
    '',
  );
}
function readProviderName(conversation: any) {
  return pickConversationField(
    conversation,
    ['provider_name', 'providerName'],
    '',
  );
}
function readProviderEmail(conversation: any) {
  return pickConversationField(conversation, ['userEmail', 'user_email'], '');
}
function readForeignLanguage(conversation: any) {
  return pickConversationField(
    conversation,
    ['foreign_language', 'foreignLanguage', 'language'],
    '',
  );
}
function readMeetingUrl(conversation: any) {
  return pickConversationField(
    conversation,
    ['meeting_url', 'meetingUrl', 'meetingURL'],
    '',
  );
}

/**
 * Resolve a human-readable label for a Conversation's contributor when
 * we have to render a single name string (segment markers, attribution
 * lines, etc.). Mirrors the precedence the SessionDetailView
 * Contributors panel uses so the transcript headers and the panel
 * never disagree about the same provider:
 *   1. `provider_name` (or camelCase `providerName`) — set explicitly
 *      by the provider's account profile.
 *   2. `userEmail` (or `user_email`) — every Conversation carries this
 *      from `createScribeConversation`, so it's an honest fallback
 *      when an account hasn't filled in a provider name yet.
 *   3. `'Unknown provider'` — last-resort sentinel; should be rare in
 *      practice because every signed-in user has an email.
 *
 * Kept as a top-level helper rather than inlined into
 * `buildSegmentMarkerMessage` so the same precedence can be reused
 * by other surfaces (e.g. Contributors panel labelFor) when they
 * migrate over.
 */
export function resolveContributorLabel(conversation: any) {
  const name = readProviderName(conversation).trim();
  if (name) return name;
  const email = readProviderEmail(conversation).trim();
  if (email) return email;
  return 'Unknown provider';
}

export function toJsDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export function implicitEncounterId(
  conversationId: string | null | undefined,
): string | null {
  if (!conversationId) return null;
  return `${IMPLICIT_PREFIX}${conversationId}`;
}

export function isImplicitEncounterId(id: unknown): boolean {
  return typeof id === 'string' && id.startsWith(IMPLICIT_PREFIX);
}

/**
 * Returns `{ user_id, provider_name, conversation_count, total_minutes,
 * first_at, last_at }` for every distinct contributor across a conversation
 * list. Stable-sorted by first contribution time so the provider who started
 * the encounter shows first.
 */
export function extractContributors(conversations: any[] = []) {
  const byUser = new Map();
  const ordered = [...conversations].sort((a, b) => {
    const ta = toJsDate(a?.start_time)?.getTime() ?? 0;
    const tb = toJsDate(b?.start_time)?.getTime() ?? 0;
    return ta - tb;
  });

  ordered.forEach((conv, idx) => {
    if (!conv) return;
    const userId = conv.user_id || '__unknown__';
    const providerName = readProviderName(conv);
    if (!byUser.has(userId)) {
      byUser.set(userId, {
        user_id: userId,
        provider_name: providerName,
        userEmail: conv.userEmail || '',
        conversation_count: 0,
        total_minutes: 0,
        first_at: null,
        last_at: null,
        first_order: idx,
      });
    }
    const entry = byUser.get(userId);
    entry.conversation_count += 1;
    entry.total_minutes += Number(conv.minutes) || 0;
    const start = toJsDate(conv.start_time);
    const end = toJsDate(conv.end_time) || start;
    if (start && (!entry.first_at || start < entry.first_at))
      entry.first_at = start;
    if (end && (!entry.last_at || end > entry.last_at)) entry.last_at = end;
    if (!entry.provider_name && providerName)
      entry.provider_name = providerName;
    if (!entry.userEmail && conv.userEmail) entry.userEmail = conv.userEmail;
  });

  return Array.from(byUser.values()).sort(
    (a, b) => a.first_order - b.first_order,
  );
}

/**
 * Returns the "role" label we use in segment headers for the Nth conversation
 * in chronological order out of `total` conversations. Intentionally
 * non-prescriptive — we don't actually know what each session was clinically
 * (chart prep, visit, follow-up, etc.), so the label is purely positional.
 *   1 of 1  → 'Session'
 *   N of M  → 'Session N of M'
 */
export function inferSegmentRole(index: number, total: number) {
  if (total <= 1) return 'Session';
  return `Session ${index + 1} of ${total}`;
}

// Workflow types that are live encounter sessions. Anything else
// (medical_scribing, telehealth_scribe, blank/legacy) is a scribe modality.
// Kept local to this module so the segment-header label stays in sync with how
// `useContinueEncounter` and the EncounterCard badge classify modalities.
const LIVE_ENCOUNTER_WORKFLOWS = new Set([
  LIVE_ENCOUNTER_WORKFLOW_TYPE,
  TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE,
]);

// Workflow types recorded by the two scribe modalities:
//   - 'medical_scribing'  — the in-person / in-app Scribe (web ScribePage,
//     iOS scribe).
//   - 'telehealth_scribe' — the Zoom/Meet/Teams "scribe bot" that joins a
//     video call and silently transcribes it.
// Both count as "Scribe" for filtering and accent-coloring. We keep
// `telehealth_scribe` distinct internally so workflow-specific logic can still
// branch, but user-facing labels stay plain "Scribe".
// 'dictation' counts as a scribe modality too: it's the same transcription
// pipeline with the provider as the only speaker, so filtering, accent
// coloring, and charting labels (a non-scribe workflow gets an "Interpreter"
// label there) all treat it as Scribe.
const SCRIBE_WORKFLOWS = new Set([
  MEDICAL_SCRIBING_WORKFLOW_TYPE,
  TELEHEALTH_SCRIBE_WORKFLOW_TYPE,
  DICTATION_WORKFLOW_TYPE,
]);

// Read the workflow type off a session/encounter-like object (either field
// casing, via `readWorkflowType`) or accept a bare workflow-type string.
function coerceWorkflowType(input: any) {
  return typeof input === 'string' ? input : readWorkflowType(input);
}

function collectSessionLikeObjects(input: any) {
  if (!input || typeof input === 'string') return [];
  return [
    input,
    input.__encounterContext,
    input.scheduledBot,
    ...(input.conversations || []),
    ...(input.__encounterContext?.conversations || []),
  ].filter(Boolean);
}

function isZoomMeetingUrl(value: unknown): boolean {
  if (!value) return false;
  try {
    const hostname = new URL(value as string).hostname.toLowerCase();
    return hostname === 'zoom.us' || hostname.endsWith('.zoom.us');
  } catch {
    return String(value).toLowerCase().includes('zoom.us');
  }
}

export function hasZoomMeeting(input: any): boolean {
  if (typeof input === 'string') return isZoomMeetingUrl(input);
  return collectSessionLikeObjects(input).some(item =>
    isZoomMeetingUrl(readMeetingUrl(item)),
  );
}

/**
 * True when a session/encounter carries a link to any supported video-call
 * platform (Zoom, Google Meet, Teams, Webex, etc.) — not just Zoom. This is
 * the signal that a visit happened over video, regardless of which provider
 * hosted the call.
 */
export function hasVideoMeeting(input: any): boolean {
  if (typeof input === 'string') return isSupportedVideoCallLink(input);
  return collectSessionLikeObjects(input).some(item =>
    isSupportedVideoCallLink(readMeetingUrl(item)),
  );
}

/**
 * True when a session/encounter is a telehealth (video) visit. Prefer the
 * explicit workflow type when present, and fall back to a video-call meeting
 * link for records where the link is the only telehealth signal. We detect
 * every supported video platform here (not just Zoom) so a Google Meet or
 * Teams visit is still recognized as telehealth.
 */
export function isTelehealthEncounter(input: any): boolean {
  if (typeof input === 'string') {
    return (
      input.toLowerCase().includes('telehealth') ||
      isSupportedVideoCallLink(input)
    );
  }
  return (
    collectSessionLikeObjects(input).some(
      item =>
        item?.isTelehealth === true ||
        item?.is_telehealth === true ||
        readWorkflowType(item).toLowerCase().includes('telehealth'),
    ) || hasVideoMeeting(input)
  );
}

/**
 * True when the session/encounter (or bare workflow-type string) is either
 * scribe modality (in-room `medical_scribing` or `telehealth_scribe`).
 */
export function isScribeWorkflow(input: any): boolean {
  return SCRIBE_WORKFLOWS.has(coerceWorkflowType(input));
}

/**
 * True when the session/encounter (or bare workflow-type string) is a solo
 * provider dictation. Dictation is a scribe modality for filtering/accents
 * (see SCRIBE_WORKFLOWS) but gets its own user-facing label — "In-person"
 * would misdescribe a session with no patient interaction at all.
 */
export function isDictationWorkflow(input: any): boolean {
  return coerceWorkflowType(input) === DICTATION_WORKFLOW_TYPE;
}

/**
 * True when a session/encounter was conducted over the phone. Telephonic
 * conversations are written with `isTelephonic == true` (see
 * telephonicService); we read it off the record or any of its member
 * conversations so an aggregated Encounter view still resolves to "phone".
 */
export function isPhoneEncounter(input: any): boolean {
  if (!input || typeof input === 'string') return false;
  return collectSessionLikeObjects(input).some(
    item => item?.isTelephonic === true || item?.is_telephonic === true,
  );
}

/**
 * Canonical, user-facing modality descriptor for an encounter/session.
 * Returns `{ isScribe, isTelehealthScribe, isDictation, badge, title }`:
 *   - `badge`: short chip text — 'Dictation' | 'Scribe' | 'Encounter'
 *   - `title`: the heading the detail view shows ("<X> session")
 *   - `isScribe`: whether it's a scribe modality (drives the accent color)
 *
 * Telehealth scribe remains distinguishable via `isTelehealthScribe`, but its
 * display label is still plain "Scribe". Dictation keeps the scribe accent
 * (`isScribe: true`) but labels as "Dictation".
 */
export function resolveEncounterModality(input: any) {
  const wf = coerceWorkflowType(input);
  const isTelehealthScribe = wf === 'telehealth_scribe';
  const isDictation = wf === DICTATION_WORKFLOW_TYPE;
  const isScribe = SCRIBE_WORKFLOWS.has(wf);
  return {
    isScribe,
    isTelehealthScribe,
    isDictation,
    badge: isDictation ? 'Dictation' : isScribe ? 'Scribe' : 'Encounter',
    title: isDictation
      ? 'Dictation session'
      : isScribe
        ? 'Scribe session'
        : 'Encounter session',
  };
}

/**
 * Human-readable modality label for a conversation's segment header. Reads the
 * conversation's own `workflow_type` (snake/camel tolerant) so a mixed-modality
 * encounter — one where a Scribe encounter was continued with a live Encounter,
 * or vice versa — renders an explicit "Scribe" vs "Encounter (Spanish)"
 * divider in the transcript instead of a positional-only "Session N" header.
 * The label also rides into the note-generation prompt (the backend includes
 * segment-marker text verbatim), giving the LLM clean provenance per section.
 */
export function resolveSegmentModalityLabel(conversation: any) {
  const wf = readWorkflowType(conversation);
  if (LIVE_ENCOUNTER_WORKFLOWS.has(wf)) {
    const language = readForeignLanguage(conversation);
    return language ? `Encounter (${language})` : 'Encounter';
  }
  return 'Scribe';
}

/**
 * Build the synthetic "segment header" message interleaved into the messages
 * array we send to the note-generation backend. The text is self-describing
 * so the backend's prompt builder doesn't need changes — when it joins
 * messages into a transcript, this row reads as a labeled section header
 * any modern LLM respects.
 *
 * The header leads with the segment's modality ("Scribe" / "Encounter (…)")
 * so a mixed scribe + live encounter is unambiguous both in the
 * transcript divider and in the generated-note prompt.
 */
export function buildSegmentMarkerMessage({
  index = 0,
  total = 1,
  conversation = null,
  roleOverride = null,
  timestamp = null,
}: {
  index?: number;
  total?: number;
  conversation?: any;
  roleOverride?: string | null;
  timestamp?: any;
} = {}) {
  const role = roleOverride || inferSegmentRole(index, total);
  const provider = resolveContributorLabel(conversation);
  const modality = resolveSegmentModalityLabel(conversation);
  const start = toJsDate(conversation?.start_time);
  const minutes = Number(conversation?.minutes) || 0;
  const timeFragment = start
    ? start.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';
  const durationFragment = minutes > 0 ? `, ${Math.max(minutes, 1)} min` : '';
  const text = `=== ${modality} · ${role} by ${provider}${durationFragment}${timeFragment ? ` — ${timeFragment}` : ''} ===`;
  const ts = toJsDate(timestamp) || start || new Date();
  return {
    id: `segment-${conversation?.id || index}-${ts.getTime()}`,
    input_text: text,
    output_text: '',
    explicit_meaning: SEGMENT_MARKER_SPEAKER,
    is_doctor: false,
    is_segment_marker: true,
    content_type: SEGMENT_MARKER_CONTENT_TYPE,
    timestamp: ts,
    source_conversation_id: conversation?.id || null,
    source_user_id: conversation?.user_id || null,
  };
}

/**
 * Normalize the patient-identity fields off a Conversation / Encounter / picker
 * object into a comparable shape. Tolerates both snake_case (web) and camelCase
 * (iOS / backend) and the `_ehrPatientId` the patient picker leaves behind.
 */
export function getPatientIdentity(obj: any) {
  const norm = (v: unknown) => (v == null ? '' : String(v).trim());
  if (!obj) return {patientId: '', ehrPatientId: '', name: ''};
  return {
    patientId: norm(obj.patient_id ?? obj.patientId),
    ehrPatientId: norm(
      obj.ehr_patient_id ?? obj.ehrPatientId ?? obj._ehrPatientId,
    ),
    name: norm(obj.patient_name ?? obj.patientName)
      .toLowerCase()
      .replace(/\s+/g, ' '),
  };
}

/**
 * True ONLY when we can positively determine that `a` and `b` describe two
 * different patients. The asymmetry matters: a positive id match always wins
 * (so a name typo on one record can't split a genuine same-patient encounter),
 * and we never *assert* a mismatch from missing data alone (so legacy records
 * that simply lack a `patient_id` aren't dropped from a legitimate
 * multi-conversation encounter).
 *
 * Comparison order:
 *   1. A shared id is the only positive proof of "same patient": equal
 *      `patient_id` OR equal `ehr_patient_id` → same.
 *   2. Else if BOTH sides are identified (each carries at least one id) but
 *      share none → cross. This includes a local-only vs EHR-only pair with the
 *      same name: a local patient and an EHR patient are never the same entity
 *      unless an id matches, which enforces strict patient separation.
 *   3. Else (at least one side has no id) fall back to `patient_name`: differ →
 *      cross; same/empty → not a provable mismatch (so legacy id-less records
 *      aren't dropped from a legitimate multi-conversation encounter).
 *
 * This is the invariant that keeps note generation from ever stitching two
 * patients into one note — the failure mode behind the SHC "two patients in
 * one note" incident, where a still-open prior session left an encounter open
 * and a new patient's session got bound to it.
 */
export function isCrossPatient(a: any, b: any): boolean {
  const x = getPatientIdentity(a);
  const y = getPatientIdentity(b);
  // A matching id always means same patient (a name typo can't split them).
  if (x.patientId && y.patientId && x.patientId === y.patientId) return false;
  if (x.ehrPatientId && y.ehrPatientId && x.ehrPatientId === y.ehrPatientId) {
    return false;
  }
  // Both sides identified but no shared id → different patients (incl. a
  // same-name local-only vs EHR-only pair). Strict separation.
  const xHasId = Boolean(x.patientId || x.ehrPatientId);
  const yHasId = Boolean(y.patientId || y.ehrPatientId);
  if (xHasId && yHasId) return true;
  // Not enough id signal on at least one side: fall back to name.
  if (x.name && y.name) return x.name !== y.name;
  return false;
}

/**
 * Defensive guard for encounter note aggregation. Given the primary
 * Conversation and the full sibling set pulled from an Encounter, return only
 * the siblings that are SAFE to fold into a single note:
 *
 *   - the primary conversation itself (always kept, even if still recording);
 *   - closed siblings (`end_time` set) that belong to the SAME patient.
 *
 * Everything else is dropped:
 *   - `cross_patient`: a different patient's session that was wrongly linked
 *     into this encounter — must never contaminate the note.
 *   - `unended`: a still-open / abandoned sibling whose transcript is partial
 *     and in-flux — folding it into a finalized note for another session is
 *     never correct ("un-ended sessions shouldn't cause this").
 *
 * Returns `{ kept, dropped }` where each `dropped` entry is
 * `{ id, reason: 'cross_patient' | 'unended' }` for telemetry.
 */
export function filterAggregatableSiblings(primary: any, siblings: any[] = []) {
  const primaryId = primary?.id ?? null;
  const kept = [];
  const dropped = [];
  for (const sibling of siblings) {
    if (!sibling) continue;
    if (primaryId && sibling.id === primaryId) {
      kept.push(sibling);
      continue;
    }
    if (isCrossPatient(primary, sibling)) {
      dropped.push({id: sibling.id || null, reason: 'cross_patient'});
      continue;
    }
    if (!sibling.end_time) {
      dropped.push({id: sibling.id || null, reason: 'unended'});
      continue;
    }
    kept.push(sibling);
  }
  return {kept, dropped};
}

/**
 * Aggregate messages from N conversations into a single chronological array
 * we hand to the note-generation backend. Conversations are ordered by
 * `start_time` asc; within each conversation, messages are kept in their
 * existing order (caller is expected to have ordered them by timestamp asc,
 * which `patientService.getSessionMessages` already does).
 *
 * A segment marker is inserted before each conversation's messages **unless
 * there is only one conversation total** — single-conversation encounters
 * shouldn't have a redundant "=== Session by Dr. X ===" header when they
 * look exactly like a legacy non-encounter generation.
 *
 * Returns: { messages, segments } where `segments` is the per-conversation
 *   metadata array the UI uses for provenance rendering.
 */
export function aggregateEncounterMessages({
  conversations = [],
  messagesByConversationId = {},
}: {
  conversations?: any[];
  messagesByConversationId?: Record<string, any[]>;
} = {}) {
  const ordered = [...conversations].sort((a, b) => {
    const ta = toJsDate(a?.start_time)?.getTime() ?? 0;
    const tb = toJsDate(b?.start_time)?.getTime() ?? 0;
    return ta - tb;
  });
  const total = ordered.length;
  const messages: any[] = [];
  const segments: any[] = [];
  ordered.forEach((conv, index) => {
    if (!conv) return;
    const convMsgs = messagesByConversationId[conv.id] || [];
    const segmentStart = messages.length;
    if (total > 1) {
      messages.push(
        buildSegmentMarkerMessage({index, total, conversation: conv}),
      );
    }
    for (const msg of convMsgs) {
      messages.push({
        ...msg,
        source_conversation_id: conv.id,
        source_user_id: conv.user_id || null,
      });
    }
    segments.push({
      conversation_id: conv.id,
      user_id: conv.user_id || null,
      provider_name: conv.provider_name || '',
      role: inferSegmentRole(index, total),
      start_time: toJsDate(conv.start_time),
      end_time: toJsDate(conv.end_time),
      minutes: Number(conv.minutes) || 0,
      message_count: convMsgs.length,
      offset: segmentStart,
    });
  });
  return {messages, segments};
}

/**
 * Pick the best Conversation to open when the user clicks an encounter
 * row in any "list of encounters" surface (Hub Encounters tab, Scribe
 * sessions list, etc.). The downstream session-detail views are keyed
 * off a single Conversation, so we have to commit to one.
 *
 * Selection order:
 *   1. The viewer's own most-recent member Conversation. Owners can
 *      always read their own Messages without rules-denial, and any
 *      dictation edits land on a Conversation doc they're authorized
 *      to write.
 *   2. Failing that, the encounter's most recent member Conversation
 *      regardless of owner. Detail views handle cross-team
 *      Conversations correctly (the encounter-aware messages loader
 *      aggregates every readable sibling), so this fall-through
 *      still produces a useful view.
 *   3. Failing that (a cross-team encounter doc the viewer has zero
 *      visible member Conversations for), the encounter view itself.
 *      Detail views tolerate an EncounterView-shaped object well
 *      enough to render the header from `start_time` and
 *      `patient_name`, and the messages-load fallback keeps the
 *      transcript pane from going blank.
 */
export function pickRepresentativeConversation(
  encounter: any,
  viewerUserId: string | null | undefined,
) {
  const conversations = encounter?.conversations || [];
  if (conversations.length === 0) return encounter;
  const byMostRecent = (a: any, b: any) => {
    const ta = toJsDate(a.start_time)?.getTime() ?? 0;
    const tb = toJsDate(b.start_time)?.getTime() ?? 0;
    return tb - ta;
  };
  if (viewerUserId) {
    const mine = conversations
      .filter((c: any) => c.user_id === viewerUserId)
      .sort(byMostRecent);
    if (mine.length > 0) return mine[0];
  }
  return [...conversations].sort(byMostRecent)[0];
}

/**
 * Build an EncounterView from a real Encounter doc + its member Conversations.
 * `isSynthetic: false`. Contributors and totals are recomputed from the
 * conversations themselves so a stale denormalized field on the Encounter
 * doc never lies to the UI.
 */
export function buildEncounterViewFromDoc(
  encounterDoc: any,
  conversations: any[],
) {
  // When we have at least one visible member conversation, derive
  // contributors from them (canonical source). When we don't — typical
  // for cross-team Encounter docs whose Conversations aren't readable
  // by the current viewer — synthesize contributors from the doc's
  // own `contributor_user_ids` so the card can still render avatars
  // + the name fallback ("Another provider").
  let contributors = extractContributors(conversations);
  if (
    contributors.length === 0 &&
    Array.isArray(encounterDoc.contributor_user_ids)
  ) {
    contributors = encounterDoc.contributor_user_ids
      .filter(Boolean)
      .map((uid: string) => ({
        user_id: uid,
        provider_name: '',
        userEmail: '',
        conversation_count: 0,
        total_minutes: 0,
        first_at: null,
        last_at: null,
      }));
  }

  const starts = conversations
    .map(c => toJsDate(c.start_time))
    .filter((d): d is Date => Boolean(d));
  const ends = conversations
    .map(c => toJsDate(c.end_time))
    .filter((d): d is Date => Boolean(d));
  // Prefer the encounter doc's own start_time when we have no
  // conversations to derive it from — same for end_time and minutes.
  const docStart = toJsDate(encounterDoc.start_time);
  const docEnd = toJsDate(encounterDoc.end_time);
  const start = starts.length
    ? new Date(Math.min(...starts.map((d: Date) => d.getTime())))
    : docStart;
  // `end_time` stays null while any member conversation is still open.
  const allClosed =
    conversations.length > 0 && conversations.every(c => Boolean(c.end_time));
  let end =
    allClosed && ends.length
      ? new Date(Math.max(...ends.map((d: Date) => d.getTime())))
      : null;
  if (!end && conversations.length === 0) {
    // No conversations visible — defer to the doc's own end_time, if
    // any. Helps the closed-encounter case render correctly when only
    // the doc is readable.
    end = docEnd;
  }
  const totalMinutesFromConvs = conversations.reduce(
    (sum, c) => sum + (Number(c.minutes) || 0),
    0,
  );
  const totalMinutes =
    totalMinutesFromConvs > 0
      ? totalMinutesFromConvs
      : Number(encounterDoc.total_minutes) || 0;
  const hasOpen = conversations.some(c => !c.end_time);

  // Status reconciliation. The Encounter doc's `status` field is the
  // authoritative source for cross-company viewers, but for any viewer
  // who can see the actual member conversations we trust those over
  // the stored field. Reason: the doc's status is set to 'open' at
  // creation and only flips to 'closed' via the explicit Mark Done
  // path. There is no write-side hook that closes the encounter when
  // its last member conversation ends, so once a Continue flow lands
  // the doc gets stamped 'open' and stays that way until somebody
  // taps Mark Done.
  //
  // Before this fix, that produced a confusing split: amehregan05
  // (in the encounter's company, can read the doc) saw "In progress"
  // while amehregan@berkeley.edu (different company, can't read the
  // doc, falls back to the visible-conversations-only synth view)
  // saw "Completed" for the same encounter. The pair of fixes lives
  // in two places:
  //   - here (read-side): when we have conversations to inspect,
  //     derive status from their end_times and treat encounterDoc.
  //     status as a fallback only.
  //   - in encounterService.endScribeEncounterIfFullyClosed (called
  //     from scribeService.endScribeConversation): when the last open
  //     member conversation ends, flip the doc itself to 'closed' so
  //     the persisted field stops lying to cross-company viewers.
  const liveStatus = hasOpen ? ENCOUNTER_STATUS.OPEN : ENCOUNTER_STATUS.CLOSED;
  const docStatus = encounterDoc.status || null;
  const resolvedStatus =
    conversations.length > 0 ? liveStatus : docStatus || liveStatus;

  // For real Encounter docs the workflow_type is whatever the creator wrote
  // (web always uses snake_case). For the (rare) case where it's missing or
  // the doc was hand-edited, fall back to the first member conversation's
  // workflow_type so the badge doesn't lie.
  const inferredWorkflow =
    encounterDoc.workflow_type ||
    (conversations.length ? readWorkflowType(conversations[0]) : '') ||
    'medical_scribing';
  const recordsForPatientFields = [encounterDoc, ...conversations];
  // Phone-visit flag: prefer the denormalized roll-up on the Encounter doc
  // (set by createEncounter / attachConversationToEncounter), and fall back to
  // any member conversation. Surfacing it on the view itself means
  // `isPhoneEncounter` resolves correctly even for cross-team/company viewers
  // whose member Conversations aren't readable (doc-only views).
  const isTelephonic =
    encounterDoc.isTelephonic === true ||
    encounterDoc.is_telephonic === true ||
    conversations.some(
      c => c?.isTelephonic === true || c?.is_telephonic === true,
    );
  return {
    id: encounterDoc.id,
    isSynthetic: false,
    isTelephonic,
    patient_id: encounterDoc.patient_id || null,
    ehr_patient_id: encounterDoc.ehr_patient_id || null,
    // Fall back to a member conversation's denormalized name — Schema V2
    // patient-history rows (GET /patients/:id/encounters) don't repeat the
    // patient name on each encounter row.
    patient_name:
      encounterDoc.patient_name ||
      pickEncounterField(conversations, ['patient_name', 'patientName'], '') ||
      '',
    patient_mrn: pickEncounterField(
      recordsForPatientFields,
      ['patient_mrn', 'patientMrn', 'mrn'],
      '',
    ),
    patient_dob: pickEncounterField(
      recordsForPatientFields,
      ['patient_dob', 'patientDob', 'date_of_birth', 'dateOfBirth', 'dob'],
      null,
    ),
    company_id: encounterDoc.company_id || '',
    facility_id: encounterDoc.facility_id || '',
    workflow_type: inferredWorkflow,
    template_type:
      encounterDoc.template_type ||
      (conversations.length ? readTemplateType(conversations[0]) : '') ||
      '',
    specialty: encounterDoc.specialty || '',
    foreign_language: conversations.length
      ? readForeignLanguage(conversations[0])
      : '',
    contributors,
    contributor_user_ids: contributors.map(c => c.user_id),
    conversation_ids: conversations.length
      ? conversations.map(c => c.id)
      : Array.isArray(encounterDoc.conversation_ids)
        ? encounterDoc.conversation_ids
        : [],
    conversations,
    status: resolvedStatus,
    start_time: start,
    end_time: end,
    total_minutes: totalMinutes,
    summary:
      encounterDoc.summary ||
      conversations[conversations.length - 1]?.summary ||
      '',
    is_removed: Boolean(encounterDoc.is_removed),
    has_open_session: hasOpen,
    created_at: toJsDate(encounterDoc.created_at) || start,
    updated_at: toJsDate(encounterDoc.updated_at) || end,
  };
}

/**
 * Synthesize an EncounterView from a single Conversation that has no
 * `encounter_id`. Lets the UI render legacy sessions through the same
 * components as real encounters without any data migration.
 */
export function buildImplicitEncounterView(conversation: any) {
  const contributors = extractContributors([conversation]);
  const start = toJsDate(conversation.start_time);
  const end = toJsDate(conversation.end_time);
  return {
    id: implicitEncounterId(conversation.id),
    isSynthetic: true,
    isTelephonic:
      conversation.isTelephonic === true || conversation.is_telephonic === true,
    patient_id: conversation.patient_id || null,
    ehr_patient_id: conversation.ehr_patient_id || null,
    patient_name: conversation.patient_name || '',
    patient_mrn: pickEncounterField(
      [conversation],
      ['patient_mrn', 'patientMrn', 'mrn'],
      '',
    ),
    patient_dob: pickEncounterField(
      [conversation],
      ['patient_dob', 'patientDob', 'date_of_birth', 'dateOfBirth', 'dob'],
      null,
    ),
    company_id: conversation.company_id || '',
    facility_id: conversation.facility_id || '',
    workflow_type: readWorkflowType(conversation) || 'medical_scribing',
    template_type: readTemplateType(conversation) || '',
    specialty: conversation.specialty || '',
    foreign_language: readForeignLanguage(conversation),
    contributors,
    contributor_user_ids: contributors.map(c => c.user_id),
    conversation_ids: [conversation.id],
    conversations: [conversation],
    status: conversation.end_time
      ? ENCOUNTER_STATUS.CLOSED
      : ENCOUNTER_STATUS.OPEN,
    start_time: start,
    end_time: end,
    total_minutes: Number(conversation.minutes) || 0,
    summary: conversation.summary || '',
    is_removed: Boolean(conversation.is_removed),
    has_open_session: !conversation.end_time,
    created_at: start,
    updated_at: end || start,
  };
}

/**
 * Group a flat list of Conversation docs into EncounterView objects.
 *
 *  - Conversations with `encounter_id` are bucketed under that id; if the
 *    matching Encounter doc is supplied in `encounterDocsById`, its metadata
 *    is preferred over the inferred values. If not supplied, a best-effort
 *    view is built from the conversations themselves.
 *  - Conversations without `encounter_id` become their own implicit
 *    single-conversation encounter.
 *
 * Output is sorted by start_time desc so the most recent encounter renders
 * at the top of patient detail lists.
 */
export function groupConversationsIntoEncounters(
  conversations: any[] = [],
  encounterDocsById: Map<string, any> | Record<string, any> = new Map(),
) {
  const lookup =
    encounterDocsById instanceof Map
      ? encounterDocsById
      : new Map(Object.entries(encounterDocsById || {}));

  // Reverse index from conversation id → encounter id, derived from each
  // Encounter doc's `conversation_ids` array. This is the recovery path
  // for the "back-link write didn't land" case in the Continue flow:
  //
  // When a provider continues a legacy single-conversation session,
  // `useContinueEncounter` issues two writes — `createEncounter` (with
  // the seed listed in `conversation_ids`) and
  // `setConversationEncounterId` to back-link the seed. The Encounter
  // create always succeeds for the calling provider, but the seed
  // back-link can fail silently (Firestore-rules denial on a colleague's
  // session, transient network blip, etc.). Without this reverse-lookup
  // the seed kept `encounter_id: undefined`, fell into the `implicits`
  // bucket below, and the patient timeline rendered TWO cards (the new
  // session under the real encounter + the seed as its own implicit
  // encounter) instead of one combined card.
  //
  // The doc itself is the source of truth for membership — if the
  // encounter references a conversation we know about, we trust it
  // even when the conversation hasn't yet been stamped with the
  // back-ref.
  const convIdToEncId = new Map();
  for (const [encId, encDoc] of lookup) {
    const memberIds = Array.isArray(encDoc?.conversation_ids)
      ? encDoc.conversation_ids
      : [];
    for (const memberId of memberIds) {
      if (memberId && !convIdToEncId.has(memberId)) {
        convIdToEncId.set(memberId, encId);
      }
    }
  }

  const byEncounterId = new Map();
  const implicits = [];
  for (const conv of conversations) {
    if (!conv) continue;
    // Prefer the conversation's own `encounter_id`. When it's missing
    // but an Encounter doc lists this conversation in its membership,
    // fall back to that linkage so the back-link race doesn't
    // double-render. See the comment above `convIdToEncId` for the
    // scenario.
    const encId = conv.encounter_id || convIdToEncId.get(conv.id) || null;
    if (encId) {
      if (!byEncounterId.has(encId)) byEncounterId.set(encId, []);
      byEncounterId.get(encId).push(conv);
    } else {
      implicits.push(buildImplicitEncounterView(conv));
    }
  }

  // Important: every Encounter doc the caller resolved should produce
  // a view, even when the current user has zero member Conversations
  // visible — that's exactly the cross-provider case (resident on
  // facility A records a session, attending on facility B opens the
  // patient and sees nothing under their own user_id, but the
  // Encounter doc itself is company-scoped readable). Without this
  // pass, those encounters silently disappeared from the timeline
  // for everyone except the original contributor.
  for (const encId of lookup.keys()) {
    if (!byEncounterId.has(encId)) {
      byEncounterId.set(encId, []);
    }
  }

  const realViews = Array.from(byEncounterId.entries())
    .map(([encId, convs]) => {
      const sorted = [...convs].sort((a, b) => {
        const ta = toJsDate(a.start_time)?.getTime() ?? 0;
        const tb = toJsDate(b.start_time)?.getTime() ?? 0;
        return ta - tb;
      });
      const docFromMap = lookup.get(encId) || {id: encId};
      return buildEncounterViewFromDoc({...docFromMap, id: encId}, sorted);
    })
    // Drop soft-removed encounters at the grouping layer so the
    // patient timeline never has to filter them out itself.
    .filter(v => !v.is_removed);

  const all = [...realViews, ...implicits];
  all.sort((a, b) => {
    const ta = a.start_time?.getTime?.() ?? 0;
    const tb = b.start_time?.getTime?.() ?? 0;
    return tb - ta;
  });
  return all;
}

/**
 * Pick the encounter (real or implicit) the provider should be offered to
 * continue when they start a new Scribe on this patient. Used by the
 * implicit prompt on session start — distinct from the explicit "Continue
 * encounter" button on the patient detail view, which uses
 * `isEncounterContinuable` directly without the time window.
 *
 * Rules:
 *   - Encounter must not be soft-removed.
 *   - Encounter's "representative time" — the most recent of start_time
 *     and end_time — must be within the lookback window. This applies
 *     uniformly to open AND closed encounters: a "still open" encounter
 *     that hasn't had activity in days is almost certainly an orphan
 *     (browser crashed mid-session, end_time never written), not an
 *     intentional pause. We'd rather have the provider explicitly Mark
 *     Done from the patient detail card than silently re-attach to a
 *     month-old orphan. Pre-fix, open encounters bypassed this check and
 *     a 6-week-old session would still show up as "Continue your earlier
 *     session?" — which providers rightly read as broken.
 *   - Encounter's company_id must match the calling provider's company.
 *     Caller should already pre-filter; we double-check defensively.
 *   - Implicit (synthesized) single-conversation encounters are included so
 *     the "continue from Dr. Singh's standalone prep session" flow still
 *     works against legacy data without an Encounter doc.
 *
 * Returns the encounter view (or null when nothing is eligible).
 */
export function pickContinuableEncounter(
  encounterViews: any[] = [],
  opts: Record<string, any> = {},
) {
  const {
    now = new Date(),
    lookbackMs = OPEN_ENCOUNTER_LOOKBACK_MS,
    companyId = null,
  } = opts;
  const cutoff = now.getTime() - lookbackMs;
  const candidates = encounterViews
    .filter(e => !e.is_removed)
    .filter(e => !companyId || !e.company_id || e.company_id === companyId)
    .filter(e => {
      // For open encounters, use start_time as the recency anchor (they
      // have no end_time yet by definition). For closed ones, prefer
      // end_time. Either way we hard-bound to the lookback so an
      // orphaned "open since April 12" record can't sneak in.
      const ref = e.end_time || e.start_time;
      const latest = ref?.getTime?.() ?? 0;
      return latest >= cutoff;
    })
    .sort((a, b) => {
      const ta = (a.end_time || a.start_time)?.getTime?.() ?? 0;
      const tb = (b.end_time || b.start_time)?.getTime?.() ?? 0;
      return tb - ta;
    });
  return candidates[0] || null;
}

/**
 * True when the provider should be allowed to add a new Conversation to
 * this encounter — i.e. it has no Conversation that's still recording.
 * Used by the explicit "Continue encounter" buttons on the patient detail
 * view: every closed encounter is continuable forever, but an encounter
 * with an open member is NOT continuable (we can't have two recordings
 * landing on the same encounter in parallel — the second start would race
 * the first one's `end_time` write).
 *
 * Soft-removed encounters are never continuable, and implicit encounters
 * (legacy standalone Conversations) are continuable the same as real ones
 * — `useContinueEncounter` materializes the Encounter doc just-in-time
 * before the new Conversation joins.
 */
export function isEncounterContinuable(encounter: any): boolean {
  if (!encounter) return false;
  if (encounter.is_removed) return false;
  if (encounter.has_open_session) return false;
  if (encounter.status === ENCOUNTER_STATUS.OPEN) return false;
  return true;
}

/**
 * True when the encounter was stamped `visibility: 'private'` at creation —
 * i.e. its creator's session sharing is restricted (behavioral-health
 * privacy; see `User.session_sharing_restricted` and the super-admin
 * Session Privacy panel). Absence of the field means the normal
 * company-shared behavior, so legacy docs are unaffected.
 */
export function isEncounterPrivate(encounter: any): boolean {
  return encounter?.visibility === 'private';
}

/**
 * Whether one encounter may be shown to `viewer` on shared surfaces
 * (company recents/search, patient timeline, today's-encounter pickers).
 *
 * Private encounters stay visible to their contributors (the creator plus
 * anyone who recorded into the encounter before it went private — yanking
 * a co-author's access would orphan their own contribution) and to
 * superadmins (internal support). Everyone else in the company must not
 * see the row at all — not a redacted stub — so the fact that the visit
 * happened isn't browsable either.
 *
 * This is UI-level privacy by design: Firestore rules still allow the
 * company-wide read (v1 keeps rules untouched). Callers filter with this
 * helper at every list boundary; `useContinueEncounter` re-checks it as
 * defense in depth.
 */
export function isEncounterVisibleToUser(
  encounter: any,
  viewer: {userId?: string | null; role?: string | null} | null | undefined,
): boolean {
  if (!isEncounterPrivate(encounter)) return true;
  if (!viewer) return false;
  if (viewer.role === 'superadmin') return true;
  const userId = viewer.userId || null;
  if (!userId) return false;
  const contributors = Array.isArray(encounter?.contributor_user_ids)
    ? encounter.contributor_user_ids
    : [];
  return contributors.includes(userId);
}

/**
 * List-boundary companion to `isEncounterVisibleToUser`. `viewer` is
 * optional so read paths that predate the privacy feature keep their exact
 * behavior until their callers thread a viewer through — passing no viewer
 * filters nothing (legacy shape), passing a viewer applies the privacy
 * filter.
 */
export function filterEncountersForViewer(
  encounters: any[] | null | undefined,
  viewer: {userId?: string | null; role?: string | null} | null | undefined,
): any[] {
  const list = Array.isArray(encounters) ? encounters : [];
  if (!viewer) return list;
  return list.filter(encounter => isEncounterVisibleToUser(encounter, viewer));
}

/**
 * True when the encounter is currently in the recording state — i.e. at
 * least one Conversation has not yet been ended. UI surfaces should
 * render a "Recording in progress" indicator (greyed, non-interactive)
 * instead of a Continue button when this is true.
 */
export function isEncounterRecording(encounter: any): boolean {
  if (!encounter) return false;
  if (encounter.is_removed) return false;
  return (
    Boolean(encounter.has_open_session) ||
    encounter.status === ENCOUNTER_STATUS.OPEN
  );
}

/**
 * True when the encounter's representative time (end_time if closed,
 * start_time otherwise) is in the same calendar day as `now` in the
 * caller's local timezone. Drives the "Today's encounter" prominence on
 * the patient detail view — everything else falls into the "Earlier"
 * section. We use the caller's local timezone (rather than the facility
 * timezone) because the provider's mental model of "today" is the wall
 * clock they're looking at; that's also how appointment views elsewhere
 * in the app render calendar dates.
 */
export function isTodaysEncounter(
  encounter: any,
  now: Date = new Date(),
): boolean {
  if (!encounter) return false;
  const ref = encounter.end_time || encounter.start_time;
  if (!ref) return false;
  const refDate = ref instanceof Date ? ref : toJsDate(ref);
  if (!refDate) return false;
  return (
    refDate.getFullYear() === now.getFullYear() &&
    refDate.getMonth() === now.getMonth() &&
    refDate.getDate() === now.getDate()
  );
}

/**
 * Pick the single "Today's encounter" to surface in the prominent slot at
 * the top of the patient detail view. Rules:
 *   - Must be from today (in the caller's local timezone).
 *   - Soft-removed encounters are excluded.
 *   - Prefer recording-in-progress encounters when present, since the
 *     "Recording in progress" indicator is the most actionable signal.
 *   - Otherwise prefer the most-recently-active encounter.
 *
 * Returns the encounter view, or null when the patient has nothing today.
 */
export function pickTodaysEncounter(
  encounterViews: any[] = [],
  now: Date = new Date(),
) {
  const todays = encounterViews
    .filter(e => !e.is_removed)
    .filter(e => isTodaysEncounter(e, now));
  if (todays.length === 0) return null;
  // Recording-in-progress always wins — that's the encounter the provider
  // most likely needs to act on right now (either continue or mark done).
  const recording = todays.find(isEncounterRecording);
  if (recording) return recording;
  return todays.sort((a, b) => {
    const ta = (a.end_time || a.start_time)?.getTime?.() ?? 0;
    const tb = (b.end_time || b.start_time)?.getTime?.() ?? 0;
    return tb - ta;
  })[0];
}
